import { isDefiniteShopRejection } from "./shopMutationOutcome";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import type { User } from "firebase/auth";
import { firebaseAuth } from "../../../components/firebase/auth";
import { firebaseStore } from "../../../components/firebase/firestore";
import { ShopApiError, shopAdminApi, shopStockApiConfigured } from "./shopAdminApi";
import { stockSaleStatusId, type StockSaleStatusSnapshot, type StockSaleStatus } from "./stockSaleStatusModel";
import StockAffectedOrders from "./StockAffectedOrders";
import { packagingLanguageLabel } from "../processedStockModel";
import "./webshopCatalog.css";

type Row = { id: string; data: Record<string, unknown> };
const string = (value: unknown) => typeof value === "string" ? value.trim() : "";
const count = (value: unknown) => Number.isSafeInteger(value) && (value as number) >= 0 ? value as number : null;

type Intent = { uid: string; id: string; body: { expectedRevision: number; shopSaleStatus: StockSaleStatus; reason: string }; key: string; inFlight: boolean; uncertain?: boolean };
export default function WebshopInventory({ canManage = false, canReadOrders = false, previewOnly = false }: { canManage?: boolean; canReadOrders?: boolean; previewOnly?: boolean }) {
  const [user] = useAuthState(firebaseAuth);
  const intent = useRef<Intent | null>(null);
  const [intentVersion, setIntentVersion] = useState(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  return <InventoryPage key={`${user?.uid || "signed-out"}:${canManage}:${canReadOrders}:${previewOnly}`} user={user || null}
    canManage={canManage && !previewOnly} canReadOrders={canReadOrders && !previewOnly} intent={intent} intentVersion={intentVersion}
    onIntentSettled={() => { if (mounted.current) setIntentVersion((value) => value + 1); }} />;
}
function InventoryPage({ user, canManage, canReadOrders, intent, intentVersion, onIntentSettled }: { user: User | null; canManage: boolean; canReadOrders: boolean;
  intent: React.MutableRefObject<Intent | null>; intentVersion: number; onIntentSettled: () => void }) {
  const [stock, setStock] = useState<Row[]>([]);
  const [packages, setPackages] = useState<Row[]>([]);
  const [loaded, setLoaded] = useState({ stock: false, packages: false });
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [saleStatus, setSaleStatus] = useState<"approved" | "hold" | "recalled">("hold");
  const [reason, setReason] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState<StockSaleStatusSnapshot | null>(null);
  const [reading, setReading] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [pending, setPending] = useState(!!intent.current);
  const [denied, setDenied] = useState(false);
  const [reload, setReload] = useState(0);
  const live = useRef(true);
  const editorRequest = useRef(0);
  const [affectedStock, setAffectedStock] = useState<Row | null>(null);
  useEffect(() => { setPending(!!intent.current); setSaving(!!intent.current?.inFlight);
    if (!intent.current && editing && pending && !rejected) {
      setRejected(true); setSnapshot(null); setSaveError("Den tidigare ändringen har avslutats. Hämta aktuell lagerpost och granska igen.");
    }
    // Settlement is a signal only; a former user's result never hydrates this editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, intentVersion]);
  const hideDenied = () => {
    setDenied(true); setStock([]); setPackages([]); setEditing(null); setSnapshot(null); setAffectedStock(null);
    editorRequest.current++;
  };
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);
  useEffect(() => {
    if (!user || denied) return;
    let active = true;
    const stopStock = onSnapshot(collection(firebaseStore, "products_freezedry_batch"), (snapshot) => {
      if (!active) return;
      setStock(snapshot.docs.map((doc) => ({ id: doc.id, data: doc.data() })));
      setLoaded((current) => ({ ...current, stock: true }));
    }, () => { if (active) hideDenied(); });
    const stopPackages = onSnapshot(collection(firebaseStore, "products_freezedry_package"), (snapshot) => {
      if (!active) return;
      setPackages(snapshot.docs.map((doc) => ({ id: doc.id, data: doc.data() })));
      setLoaded((current) => ({ ...current, packages: true }));
    }, () => { if (active) hideDenied(); });
    return () => { active = false; stopStock(); stopPackages(); };
    // The keyed page owns one user/capability context; denied reads require an explicit reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, denied, reload]);

  const rows = useMemo(() => [...stock].sort((a, b) => string(a.data.bestBeforeDate).localeCompare(string(b.data.bestBeforeDate)) || a.id.localeCompare(b.id)), [stock]);
  const packageName = (packageId: string) => {
    const pkg = packages.find((row) => string(row.data.id) === packageId || row.id === packageId);
    return pkg ? string(pkg.data.menuName_sv) || string(pkg.data.formatedSize) || `${pkg.data.size || "?"} g` : packageId || "Okänd förpackning";
  };
  const loadStatus = async (row: Row) => {
    if (!user || !canManage || denied || intent.current || reading) return;
    const requestId = ++editorRequest.current;
    setReading(true); setSnapshot(null); setReason(""); setSaveError(""); setRejected(false);
    try {
      const fresh = await shopAdminApi.stockSaleStatus(user, row.id);
      if (!live.current || requestId !== editorRequest.current) return;
      setSnapshot(fresh); setSaleStatus(fresh.shopSaleStatus === "unreviewed" ? "hold" : fresh.shopSaleStatus);
    } catch (caught) {
      if (!live.current || requestId !== editorRequest.current) return;
      if (caught instanceof ShopApiError && [401, 403].includes(caught.status)) hideDenied();
      else setSaveError("Aktuell lagerpost kunde inte hämtas. Hämta igen innan du ändrar säljstatus.");
    } finally { if (live.current && requestId === editorRequest.current) setReading(false); }
  };
  const openStatus = (row: Row) => {
    if (!canManage || denied || !stockSaleStatusId(row.id)) return;
    if (intent.current && (intent.current.uid !== user?.uid || intent.current.id !== row.id)) return;
    setEditing(row);
    if (intent.current) {
      setSaleStatus(intent.current.body.shopSaleStatus); setReason(intent.current.body.reason);
      setSaving(intent.current.inFlight); setPending(true);
      setSaveError("Sparandet kunde inte bekräftas. Försök igen med samma uppgifter innan en ny ändring.");
    } else void loadStatus(row);
  };
  const saveStatus = async () => {
    if (!user || !canManage || denied || !editing || intent.current?.inFlight || rejected || reading) return;
    if (!intent.current) {
      if (!snapshot || !reason.trim() || snapshot.revision >= Number.MAX_SAFE_INTEGER - 1) return;
      intent.current = { uid: user.uid, id: editing.id, body: { expectedRevision: snapshot.revision, shopSaleStatus: saleStatus,
        reason: reason.trim() }, key: crypto.randomUUID(), inFlight: false };
    }
    const attempt = intent.current;
    if (attempt.uid !== user.uid || attempt.id !== editing.id) return;
    attempt.inFlight = true; setSaving(true); setPending(true); setSaveError("");
    try {
      await shopAdminApi.updateStockSaleStatus(user, attempt.id, attempt.body, attempt.key);
      if (intent.current === attempt) intent.current = null;
      if (!live.current) return;
      setPending(false); setEditing(null); setSnapshot(null); setError("Ändringen är registrerad. Listan uppdateras från lagret.");
    } catch (caught) {
      const definitive = isDefiniteShopRejection(caught, attempt.uncertain);
      if (!definitive) attempt.uncertain = true;
      if (definitive && intent.current === attempt) intent.current = null;
      if (!live.current) return;
      if (caught instanceof ShopApiError && [401, 403].includes(caught.status)) { setPending(false); hideDenied(); }
      else if (definitive) { setPending(false); setRejected(true); setSnapshot(null);
        setSaveError("Ändringen avvisades. Hämta aktuell lagerpost och granska igen med en ny orsak."); }
      else setSaveError("Sparandet kunde inte bekräftas. Försök igen med samma uppgifter innan en ny ändring.");
    } finally { attempt.inFlight = false; if (live.current) setSaving(false); onIntentSettled(); }
  };
  if (!user) return <Alert severity="warning">Logga in för att läsa lagret.</Alert>;
  if (denied) return <Alert severity="error">Lagerbehörigheten kunde inte verifieras. Inga sparade uppgifter visas.
    <Button onClick={() => { setDenied(false); setReading(false); setLoaded({ stock: false, packages: false }); setReload((value) => value + 1); }}>Hämta aktuell behörighet</Button></Alert>;
  return <div className="DashBoardTabInner FreshDashboardTab WebshopCatalog">
    <div className="FreshTabToolbar"><div><p className="FreshEyebrow">Webshop</p><h2 className="FreshTabTitle">Säljbarhet</h2>
      <p className="FreshTabSummary">Samma lagerposter och reservationstal som i Processat lager. Granskning och försäljningsspärrar styr vilka poster som får säljas.</p></div>
      <Button component={Link} to="/processed/stock">Öppna Processat lager</Button></div>
    <Alert severity="info">Säljstatus, lagerändringar och berörda ordrar använder skyddat API när det är driftsatt. Förhandsvisningen är endast läsbar. Köp är avstängda.</Alert>
    {canManage && !canReadOrders && <Alert severity="info">Berörda ordrar kräver även behörighet att granska ordrar.</Alert>}
    {error && <Alert severity="success">{error}</Alert>}
    {pending && <Alert severity="warning">En statusändring är obekräftad. Återgå till samma lagerpost och försök igen med samma uppgifter. Efter en fullständig omladdning av sidan måste aktuella uppgifter granskas innan en ny ändring.</Alert>}
    {!loaded.stock || !loaded.packages ? <div role="status"><CircularProgress size={24} /> Hämtar lager…</div> :
      <div className="WebshopCatalogList">{rows.length === 0 && !error && <p>Inga processade lagerposter finns ännu.</p>}
        {rows.map((row) => {
          const quantity = count(row.data.quantity);
          const reserved = count(row.data.reservedQuantity);
          const invalid = quantity === null || reserved === null || reserved > quantity;
          const status = string(row.data.shopSaleStatus);
          const approved = status === "approved";
          const expired = !!string(row.data.bestBeforeDate) && string(row.data.bestBeforeDate) < new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm", year: "numeric", month: "2-digit", day: "2-digit" });
          return <article className="WebshopCatalogProduct" key={row.id}>
            <div className="WebshopCatalogProductHeader"><div><h3>{packageName(string(row.data.packageId))}</h3>
              <p>Batch {string(row.data.batchId) || row.id} · Bäst före {string(row.data.bestBeforeDate) || "saknas"}</p>
              <p>Förpackningsspråk: {packagingLanguageLabel(row.data.packagingLanguage)}</p>
              <strong>{status === "recalled" ? "Återkallad" : status === "hold" ? "Spärrad" : approved ? "Godkänd" : "Ej granskad"}</strong></div>
              <div><Button component={Link} to="/processed/stock" size="small">Visa i Processat lager</Button>
                {canManage && shopStockApiConfigured() && <><Button size="small" disabled={!stockSaleStatusId(row.id) || (!!intent.current && (intent.current.uid !== user.uid || intent.current.id !== row.id))} onClick={() => openStatus(row)}>Ändra säljstatus</Button>
                  <Button size="small" disabled={!canReadOrders || !stockSaleStatusId(row.id)} onClick={() => setAffectedStock(row)}>Berörda ordrar</Button></>}</div></div>
            <p>Fysiskt: {quantity ?? "ogiltigt"} · Reserverat: {reserved ?? "ogiltigt"} · Ej reserverat: {invalid ? "okänt" : Math.max(0, quantity! - reserved!)}</p>
            {(invalid || expired || !string(row.data.bestBeforeDate) || !approved) && <p className="WebshopCatalogReasons">{[
              invalid && "Ogiltiga antal eller reservationer", expired && "Bäst före har passerat",
              !string(row.data.bestBeforeDate) && "Bäst före saknas", !approved && "Inte säljbar i webbshoppen",
            ].filter(Boolean).join(" · ")}</p>}
          </article>;
        })}
      </div>}
    <Dialog open={!!editing} onClose={() => { if (!pending && !saving) { editorRequest.current++; setReading(false); setEditing(null); } }} fullWidth maxWidth="sm">
      <DialogTitle>Ändra säljstatus</DialogTitle>
      <DialogContent className="WebshopOfferDialog">
        <p>{editing && packageName(string(editing.data.packageId))}</p>
        {reading && <p role="status">Hämtar aktuell lagerpost…</p>}
        {snapshot && <p>Revision {snapshot.revision} · Fysiskt {snapshot.quantity} · Reserverat {snapshot.reservedQuantity}</p>}
        {snapshot && snapshot.revision >= Number.MAX_SAFE_INTEGER - 1 && <Alert severity="warning">Revisionen kan inte höjas säkert. Lagerposten behöver utredas innan en ny statusändring.</Alert>}
        <TextField select fullWidth size="small" label="Status" value={saleStatus} onChange={(event) => setSaleStatus(event.target.value as StockSaleStatus)} disabled={pending || !snapshot || reading}>
          <MenuItem value="approved">Godkänd för försäljning</MenuItem><MenuItem value="hold">Spärrad</MenuItem><MenuItem value="recalled">Återkallad</MenuItem>
        </TextField>
        <TextField fullWidth size="small" multiline minRows={2} label="Orsak *" value={reason} onChange={(event) => setReason(event.target.value)} inputProps={{ maxLength: 500 }} disabled={pending || !snapshot || reading} />
        {saveError && <Alert severity="error">{saveError}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={() => { editorRequest.current++; setReading(false); setEditing(null); }} disabled={pending || saving}>Avbryt</Button>
        {!pending && <Button onClick={() => editing && void loadStatus(editing)} disabled={reading}>Hämta aktuell lagerpost</Button>}
        <Button onClick={saveStatus} disabled={saving || reading || rejected || (!pending && (!snapshot || !reason.trim() || snapshot.revision >= Number.MAX_SAFE_INTEGER - 1))}>
          {pending ? "Försök igen med samma uppgifter" : "Spara"}</Button></DialogActions>
    </Dialog>
    {affectedStock && canManage && canReadOrders && <StockAffectedOrders key={affectedStock.id} user={user} stockDocId={affectedStock.id}
      packageName={packageName(string(affectedStock.data.packageId))} onClose={() => setAffectedStock(null)} onDenied={hideDenied} />}
  </div>;
}
